import React from 'react'
import { InboxSuccess } from './Inbox/InboxSuccess'
import { InboxUpsellBody, InboxUpsellFeatures } from './Inbox/InboxUpsell'
import { ProfessionalSuccess } from './Professional/ProfessionalSuccess'
import { InboxTimeline } from './Inbox/InboxTimeline'
import { DeltaTrialTimelineProps } from './Timeline/DeltaTrialTimeline'
import { ProfessionalTimeline } from './Professional/ProfessionalTimeline'
import { ProcessDocumentUpsellBody, ProcessDocumentUpsellFeatures } from './ProcessDocument/ProcessDocumentUpsell'
import { ProcessDocumentTimeline } from './ProcessDocument/ProcessDocumentTimeline'
import { ProcessDocumentSuccess } from './ProcessDocument/ProcessDocumentSuccess'
import { FormTemplateUpsellBody, FormTemplateUpsellFeatures } from './FormTemplate/FormTemplateUpsell'
import { FormTemplateTimeline } from './FormTemplate/FormTemplateTimeline'
import { FormTemplateSuccess } from './FormTemplate/FormTemplateSuccess'
import { BusinessEventsUpsellBody, BusinessEventsUpsellFeatures } from './BusinessEvents/BusinessEventsUpsell'
import { BusinessEventsTimeline } from './BusinessEvents/BusinessEventsTimeline'
import { BusinessEventsSuccess } from './BusinessEvents/BusinessEventsSuccess'
import { ZapierIntegrationUpsellBody, ZapierIntegrationUpsellFeatures } from './ZapierIntegration/ZapierIntegration'
import { ZapierIntegrationTimeline } from './ZapierIntegration/ZapierIntegrationTimeline'
import { ZapierIntegrationSuccess } from './ZapierIntegration/ZapierIntegrationSuccess'
import { AnalyticsUpsellBody, AnalyticsUpsellFeatures } from './Analytics/AnalyticsUpsell'
import { AnalyticsTimeline } from './Analytics/AnalyticsTimeline'
import { AnalyticsSuccess } from './Analytics/AnalyticsSuccess'
import { InsightsUpsellBody, InsightsUpsellFeatures } from './Insights/InsightsUpsell'
import { InsightsTimeline } from './Insights/InsightsTimeline'
import { InsightsSuccess } from './Insights/InsightsSuccess'
import { getProfessionalPlanId, getUltimatePlanId } from '../planConstants'
import { SchedulingUpsellBody, SchedulingUpsellFeatures } from './Scheduling/SchedulingUpsell'
import { SchedulingTimeline } from './Scheduling/SchedulingTimeline'
import { SchedulingSuccess } from './Scheduling/SchedulingSuccess'
import { OAuth2AppDirectoryUpsellBody, OAuth2AppDirectoryUpsellFeatures } from './OAuth2AppDirectory/OAuth2AppDirectory'
import { OAuth2AppDirectoryTimeline } from './OAuth2AppDirectory/OAuth2AppDirectoryTimeline'
import { OAuth2AppDirectorySuccess } from './OAuth2AppDirectory/OAuth2AppDirectorySuccess'
import { CustomIntegrationUpsellFeatures } from './CustomIntegration/CustomIntegrationUpsell'
import { CustomIntegrationTimeline } from './CustomIntegration/CustomIntegrationTimeline'
import { CustomIntegrationSuccess } from './CustomIntegration/CustomIntegrationSuccess'
import { ClientQueriesUpsellBody, ClientQueriesUpsellFeatures } from './ClientQueries/ClientQueriesUpsell'
import { ClientQueriesTimeline } from './ClientQueries/ClientQueriesTimeline'
import { ClientQueriesSuccess } from './ClientQueries/ClientQueriesSuccess'

export interface DeltaTrialFeature {
  id: string
  name: string
  planId: string
  bookDemoLink: string
  UpsellBody?: React.ComponentType
  UpsellFeatures: React.ComponentType
  DeltaTrialTimeline: React.ComponentType<DeltaTrialTimelineProps>
  DeltaTrialSuccess: React.ComponentType
  upsellDescription?: (deltaTrialDays: number) => string
  secondaryButton?: { label: string; to: string }
  highlightPlanName?: boolean
}

export const DELTA_TRIAL_FEATURES = {
  inbox: {
    id: 'inbox',
    name: 'Inbox',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-ultimate?utm_medium=zocks&utm_source=in-app&utm_campaign=inbox',
    UpsellBody: InboxUpsellBody,
    UpsellFeatures: InboxUpsellFeatures,
    DeltaTrialTimeline: InboxTimeline,
    DeltaTrialSuccess: InboxSuccess,
  },
  processDocument: {
    id: 'processDocument',
    name: 'Document Intelligence',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-ultimate?utm_medium=zocks&utm_source=in-app&utm_campaign=processdocument',
    UpsellBody: ProcessDocumentUpsellBody,
    UpsellFeatures: ProcessDocumentUpsellFeatures,
    DeltaTrialTimeline: ProcessDocumentTimeline,
    DeltaTrialSuccess: ProcessDocumentSuccess,
  },
  professional: {
    id: 'professional',
    name: 'Professional features',
    planId: getProfessionalPlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-professional?utm_medium=zocks&utm_source=in-app&utm_campaign=professional',
    UpsellBody: () => null,
    UpsellFeatures: () => null,
    DeltaTrialTimeline: ProfessionalTimeline,
    DeltaTrialSuccess: ProfessionalSuccess,
  },
  formTemplate: {
    id: 'formTemplate',
    name: 'Document Fill',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-ultimate?utm_medium=zocks&utm_source=in-app&utm_campaign=formtemplate',
    UpsellBody: FormTemplateUpsellBody,
    UpsellFeatures: FormTemplateUpsellFeatures,
    DeltaTrialTimeline: FormTemplateTimeline,
    DeltaTrialSuccess: FormTemplateSuccess,
  },
  businessEvents: {
    id: 'businessEvents',
    name: 'Client Signals',
    planId: getProfessionalPlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-professional?utm_medium=zocks&utm_source=in-app&utm_campaign=businessevents',
    UpsellBody: BusinessEventsUpsellBody,
    UpsellFeatures: BusinessEventsUpsellFeatures,
    DeltaTrialTimeline: BusinessEventsTimeline,
    DeltaTrialSuccess: BusinessEventsSuccess,
  },
  zapierIntegration: {
    id: 'zapierIntegration',
    name: 'the Zapier Integration',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-ultimate?utm_medium=zocks&utm_source=in-app&utm_campaign=zapier',
    UpsellBody: ZapierIntegrationUpsellBody,
    UpsellFeatures: ZapierIntegrationUpsellFeatures,
    DeltaTrialTimeline: ZapierIntegrationTimeline,
    DeltaTrialSuccess: ZapierIntegrationSuccess,
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    planId: getProfessionalPlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-professional?utm_medium=zocks&utm_source=in-app&utm_campaign=analytics',
    UpsellBody: AnalyticsUpsellBody,
    UpsellFeatures: AnalyticsUpsellFeatures,
    DeltaTrialTimeline: AnalyticsTimeline,
    DeltaTrialSuccess: AnalyticsSuccess,
    upsellDescription: (deltaTrialDays: number) => `Start a free ${deltaTrialDays}-day trial and see what's happening across your practice:`,
    secondaryButton: { label: 'View plan details', to: '/account/plan' },
  },
  insights: {
    id: 'insights',
    name: 'Insights',
    planId: getProfessionalPlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-professional?utm_medium=zocks&utm_source=in-app&utm_campaign=insights',
    UpsellBody: InsightsUpsellBody,
    UpsellFeatures: InsightsUpsellFeatures,
    DeltaTrialTimeline: InsightsTimeline,
    DeltaTrialSuccess: InsightsSuccess,
    upsellDescription: (deltaTrialDays: number) => `Start a free ${deltaTrialDays}-day trial and see what your conversations are telling you:`,
    secondaryButton: { label: 'View plan details', to: '/account/plan' },
  },
  scheduling: {
    id: 'scheduling',
    name: 'Scheduling',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/scheduling',
    UpsellBody: SchedulingUpsellBody,
    UpsellFeatures: SchedulingUpsellFeatures,
    DeltaTrialTimeline: SchedulingTimeline,
    DeltaTrialSuccess: SchedulingSuccess,
    upsellDescription: (deltaTrialDays: number) => `Start your ${deltaTrialDays}-day trial to unlock Scheduling and save hours every week:`,
  },
  appDirectory: {
    id: 'appDirectory',
    name: 'Apps',
    planId: getProfessionalPlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-professional?utm_medium=zocks&utm_source=in-app&utm_campaign=appdirectory',
    UpsellBody: OAuth2AppDirectoryUpsellBody,
    UpsellFeatures: OAuth2AppDirectoryUpsellFeatures,
    DeltaTrialTimeline: OAuth2AppDirectoryTimeline,
    DeltaTrialSuccess: OAuth2AppDirectorySuccess,
    upsellDescription: (deltaTrialDays: number) => `Start your ${deltaTrialDays}-day trial to unlock Client Signals and save hours every week:`,
    secondaryButton: { label: 'View plan details', to: '/account/plan' },
  },
  customIntegrations: {
    id: 'customIntegrations',
    name: 'Custom MCPs',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://www.zocks.io/upgrade-to-ultimate?utm_medium=zocks&utm_source=in-app&utm_campaign=custommcp',
    UpsellFeatures: CustomIntegrationUpsellFeatures,
    DeltaTrialTimeline: CustomIntegrationTimeline,
    DeltaTrialSuccess: CustomIntegrationSuccess,
    upsellDescription: (deltaTrialDays: number) => `Start a free ${deltaTrialDays}-day trial and connect your own MCP servers to Zocks.`,
    secondaryButton: { label: 'View plan details', to: '/account/plan' },
    highlightPlanName: true,
  },
  clientQueries: {
    id: 'clientQueries',
    name: 'Client Queries',
    planId: getUltimatePlanId(),
    bookDemoLink: 'https://zocks.io/book-a-demo-client-queries',
    UpsellBody: ClientQueriesUpsellBody,
    UpsellFeatures: ClientQueriesUpsellFeatures,
    DeltaTrialTimeline: ClientQueriesTimeline,
    DeltaTrialSuccess: ClientQueriesSuccess,
    highlightPlanName: true,
  },
} satisfies Record<string, DeltaTrialFeature>

export const getDeltaTrialFeature = (id: string): DeltaTrialFeature | undefined => (DELTA_TRIAL_FEATURES as Record<string, DeltaTrialFeature>)[id]
